import React, { useMemo, useState } from 'react'
import { Button, Col, Flex, Modal, Row, Spin, Typography } from 'antd'
import { EmployeeCompactCard } from '@/pages/employee-data/components/EmployeeCard.tsx'
import { plainToInstance } from 'class-transformer'
import { EmployeePublicInfo } from '@/features/employee-data/employee-data.def.ts'
import { CloseOutlined } from '@ant-design/icons'
import { AntdFormItem, AntdFormItemProps } from '@/common/components/AntdForm/AntdFormItem.tsx'
import { EmployeeTable } from '@/pages/employee-data/components/EmployeeTable.tsx'
import { GridOptions } from 'ag-grid-community'
import { useEmployeePublicInfo } from '@/common/hooks/useEmployeePublicInfo.tsx'

type EmployeeSelectProps = {
  value?: string
  onChange?: (args?: string) => void
  disabled?: boolean
}

export const EmployeeSelect: React.FC<EmployeeSelectProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const [openSelectModal, setOpenSelectModal] = useState(false)
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>()
  const { data, isLoading } = useEmployeePublicInfo()

  const transformedData = useMemo(() => {
    return data?.map(x => plainToInstance(EmployeePublicInfo, x)) || []
  }, [data])

  const currentEmployee = transformedData.find(x => x.employeeId === value)

  const customGridOptions = useMemo<GridOptions<EmployeePublicInfo>>(() => {
    return {
      rowSelection: 'single',
      onRowClicked: (e) => {
        if (e.data) {
          setSelectedEmployeeId(e.data.employeeId)
        }
      },
    }
  }, [setSelectedEmployeeId])

  const onModalClose = () => {
    setSelectedEmployeeId(undefined)
    setOpenSelectModal(false)
  }

  const onSave = () => {
    onChange?.(selectedEmployeeId)
    setOpenSelectModal(false)
  }

  if (isLoading) {
    return <Spin />
  }

  if (currentEmployee) {
    return (
      <DeletableEmployeeCard employeeId={value!} onDelete={() => onChange?.(undefined)} />
    )
  }

  return (
    <div>
      <Button
        onClick={() => {
          setOpenSelectModal(true)
        }}
        htmlType="button"
        disabled={disabled}
      >
        Select
      </Button>
      <Modal open={openSelectModal} onCancel={onModalClose} footer={null} width={1400}>
        <Typography.Title level={3} className="mb-4">Select Employee</Typography.Title>
        <Row gutter={16}>
          <Col span={19}>
            <EmployeeTable
              disableNav
              gridOptions={customGridOptions}
            />
          </Col>
          <Col span={5}>
            <Flex style={{ height: '100%' }} vertical justify="space-between">
              <Flex gap="middle" vertical>
                <Typography.Title level={5}>Click on the row to select</Typography.Title>
                {selectedEmployeeId ? <DeletableEmployeeCard
                  employeeId={selectedEmployeeId}
                  onDelete={() => setSelectedEmployeeId(undefined)}
                /> : null}
              </Flex>
              <Flex gap="middle" justify="flex-end">
                <Button htmlType="button" type="primary" danger onClick={onModalClose}>Cancel</Button>
                <Button htmlType="button" type="primary" onClick={onSave}>Save</Button>
              </Flex>
            </Flex>
          </Col>
        </Row>
      </Modal>
    </div>
  )
}

export const DeletableEmployeeCard: React.FC<{ employeeId: string, onDelete: () => void }> = ({
  employeeId,
  onDelete,
}) => {
  return (
    <Flex className="relative">
      <EmployeeCompactCard employeeId={employeeId} className="w-full" />
      <Button
        icon={<CloseOutlined />}
        htmlType="button"
        type="text"
        onClick={onDelete}
        size="small"
        danger
        style={{ position: 'absolute', right: 0, top: 0 }}
      />
    </Flex>
  )
}

type AntdFormEmployeeSelectProps = {
  //
} & AntdFormItemProps

export const AntdFormEmployeeSelect: React.FC<AntdFormEmployeeSelectProps> = ({
  fieldProps,
  // className,
  ...props
}) => {
  return (
    <AntdFormItem {...fieldProps}>
      <EmployeeSelect {...props} />
    </AntdFormItem>
  )
}